import { ref, watch } from 'vue';

// Estado do modal "tem certeza que quer desativar?" (ENG-491). Fica FORA do
// composable de propósito: cada chamada de useMarketingOptIn() tem seus
// próprios refs, mas o modal é montado uma vez só (Shell.vue) e precisa ser
// aberto pelo toggle do header (Header.vue) e pelo card mobile
// (ScreenNotifications.vue) — um ref de módulo é o jeito mais barato de
// compartilhar sem virar store.
const isOptOutConfirmOpen = ref(false);
// Comemoração ao ATIVAR (mesmo motivo pra ser de módulo): o toaster de
// sucesso era fraco como feedback — sobe um modal parabenizando e sugerindo o
// Clube Fidelidade (UserV2MarketingOptInCelebrationModal em Shell.vue).
const isOptInCelebrationOpen = ref(false);

/**
 * Preferência de marketing (mkt_accepted_at), compartilhada pelos 3 lugares
 * que ligam/desligam a mesma coisa: o toggle "Notificações" do header V2, o
 * card "Quero receber atualizações" (Dados da conta) e o card de ofertas do
 * dashboard V2 — mesmo PATCH bff/users/self-mkt, mesmo otimista + revert no
 * catch, mesmos textos.
 */
export function useMarketingOptIn() {
  const { userAcceptsMarketing, fetchUserProfile } = useUserData();
  const { openToaster } = useLayoutStore();
  const { t } = useTexts();

  const isActive = ref(false);
  const isSaving = ref(false);

  watch(
    () => userAcceptsMarketing.value,
    (value) => {
      isActive.value = !!value;
    },
    { immediate: true },
  );

  async function setMarketing(next: boolean) {
    if (isSaving.value) return;

    isActive.value = next; // otimista; reverte no catch
    isSaving.value = true;

    const { ApiService } = useApiService();

    await ApiService.patch('bff/users/self-mkt', {
      mkt_accepted_at: next ? new Date().toISOString() : null,
    })
      .then(async () => {
        // Ativar (aceitar ofertas, ligar notificações) abre o modal de
        // comemoração nos 3 lugares que usam este composable — o modal mora
        // no Shell.vue, então só tem efeito com o painel Minha Conta V2
        // aberto. Desativar não avisa: o usuário acabou de confirmar num
        // modal, e o toaster ficava por cima do switch atrapalhando religar.
        if (next) {
          isOptInCelebrationOpen.value = true;
        }
        await fetchUserProfile({ force: true });
      })
      .catch((err: any) => {
        isActive.value = !next;
        openToaster({
          message:
            err?.data?.message || t('account.updates_section.update_error'),
          type: 'error',
        });
      })
      .finally(() => {
        isSaving.value = false;
      });
  }

  function toggle() {
    setMarketing(!isActive.value);
  }

  // Ligar continua sem fricção; desligar abre o modal listando o que o usuário
  // perde (roleta, check-in, promocodes...) e só desativa se ele confirmar
  // (UserV2MarketingOptOutModal em Shell.vue chama setMarketing(false)).
  function requestToggle() {
    if (isActive.value) {
      isOptOutConfirmOpen.value = true;

      return;
    }

    setMarketing(true);
  }

  return {
    isActive,
    isSaving,
    isOptOutConfirmOpen,
    isOptInCelebrationOpen,
    setMarketing,
    toggle,
    requestToggle,
  };
}
